import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Tooltip as RTooltip, Legend, ReferenceArea,
} from "recharts";
import {
  TrendingUp, TrendingDown, Minus, Sparkles, AlertTriangle, RefreshCw,
  Info, LineChart as LineChartIcon, CalendarClock, CalendarDays, CalendarRange, CalendarCheck2,
} from "lucide-react";

const card = "rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]";

const GRANS = [
  { v: "jour", label: "Jour", icon: CalendarClock },
  { v: "semaine", label: "Semaine", icon: CalendarDays },
  { v: "mois", label: "Mois", icon: CalendarRange },
  { v: "annee", label: "Année", icon: CalendarCheck2 },
];
const INDICATORS = [
  { v: "multi", label: "Vue combinée (multi-indicateurs)" },
  { v: "reussite_appels", label: "Taux de réussite des appels" },
  { v: "spots_decroches", label: "Taux de spots décrochés" },
  { v: "rebooking", label: "Taux de rebooking" },
];
const IND_TESTID = {
  multi: "tendances-indicateur-option-multi",
  reussite_appels: "tendances-indicateur-option-reussite-appels",
  spots_decroches: "tendances-indicateur-option-spots-decroches",
  rebooking: "tendances-indicateur-option-rebooking",
};
const DIMENSIONS = [
  { v: "global", label: "Global" },
  { v: "cdp", label: "Par CDP" },
  { v: "association", label: "Par association" },
  { v: "zone", label: "Par zone / région" },
];
const KPI_META = [
  { id: "reussite_appels", label: "Réussite des appels", accent: "#EE722E", testId: "tendances-kpi-reussite-appels" },
  { id: "spots_decroches", label: "Spots décrochés", accent: "#2F6FED", testId: "tendances-kpi-spots-decroches" },
  { id: "rebooking", label: "Rebooking", accent: "#1F9D55", testId: "tendances-kpi-rebooking" },
];

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

/* ------------------------------------------------------------------ KPI card */
function TrendKpi({ label, kpi, accent, testId }) {
  const cur = kpi?.current;
  const delta = kpi?.delta;
  const hasDelta = delta !== null && delta !== undefined;
  const dir = hasDelta ? (delta > 0 ? "up" : delta < 0 ? "down" : "flat") : "flat";
  const Icon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  const deltaCls =
    dir === "up" ? "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]"
      : dir === "down" ? "bg-[rgba(214,69,69,0.12)] text-[#9B2226]"
        : "bg-[var(--surface-2)] text-muted-ink";
  return (
    <div className={`card-hover ${card} p-4`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">{label}</span>
        <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
      </div>
      <div className="mt-1 text-2xl font-extrabold tabular-nums text-ink" data-testid={`${testId}-value`}>{fmtPct(cur)}</div>
      <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold ${deltaCls}`} data-testid={`${testId}-delta`}>
        <Icon className="h-3.5 w-3.5" />
        {hasDelta ? `${delta > 0 ? "+" : ""}${delta} pts` : "—"}
      </div>
      <div className="mt-1 text-[10px] text-muted-ink">vs période précédente</div>
    </div>
  );
}

/* ------------------------------------------------------------- Chart tooltip */
function ChartTooltip({ active, payload, label, series }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl border border-[var(--border-soft)] bg-white p-2.5 text-[12px] shadow-[var(--shadow-md)]" data-testid="tendances-chart-tooltip">
      <div className="mb-1 font-bold text-ink">{label}</div>
      {payload
        .filter((p) => p.value !== null && p.value !== undefined)
        .map((p) => {
          const meta = series.find((s) => s.id === p.dataKey);
          return (
            <div key={p.dataKey} className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: p.color || meta?.color }} />
              <span className="text-muted-ink">{meta?.label || p.dataKey}</span>
              <span className="ml-auto font-bold tabular-nums text-ink">{fmtPct(p.value)}</span>
            </div>
          );
        })}
    </div>
  );
}

/* --------------------------------------------------------------- AI insights */
const SEV = {
  critical: { cls: "border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] text-[#9B2226]", Icon: AlertTriangle },
  warning: { cls: "border-[rgba(185,137,0,0.4)] bg-[rgba(252,219,83,0.28)] text-[#7A5A00]", Icon: AlertTriangle },
  info: { cls: "border-[rgba(47,111,237,0.35)] bg-[rgba(47,111,237,0.08)] text-[#1D4ED8]", Icon: Info },
};

function AiPanel({ ai, loading, onRefresh }) {
  return (
    <div className="rounded-2xl border border-[rgba(238,114,46,0.30)] bg-[rgba(238,114,46,0.06)] p-5 shadow-[var(--shadow-sm)]" data-testid="tendances-ia-panel">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--shariti-orange)] text-white"><Sparkles className="h-4 w-4" /></span>
          <div>
            <div className="text-sm font-bold text-ink">Analyse IA</div>
            <div className="text-[11px] text-muted-ink">Tendances, anomalies, alertes & recommandations</div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading} data-testid="tendances-ia-refresh"
          className="h-7 gap-1 px-2 text-[11px] text-muted-ink hover:text-[var(--shariti-orange)]">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="tendances-ia-loading">
          <Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-16 w-full" />
          <div className="text-[11px] font-semibold text-muted-ink">Analyse en cours…</div>
        </div>
      ) : !ai || ai.available === false ? (
        <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-white/60 p-4 text-[12px] text-muted-ink" data-testid="tendances-ia-empty">
          {ai?.message || "Aucune analyse disponible sur cette période."}
        </div>
      ) : (
        <div className="space-y-4">
          {ai.summary && <p className="text-[13px] leading-relaxed text-ink" data-testid="tendances-ia-summary">{ai.summary}</p>}

          {ai.trends?.length > 0 && (
            <div data-testid="tendances-ia-tendance">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Tendances</div>
              <ul className="space-y-1">
                {ai.trends.map((t, i) => (
                  <li key={i} className="flex gap-2 text-[12px] text-ink">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--shariti-orange)]" />{t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {ai.anomalies?.length > 0 && (
            <div data-testid="tendances-ia-anomalies">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Anomalies détectées</div>
              <div className="space-y-1.5">
                {ai.anomalies.map((a, i) => {
                  const s = SEV[a.severity] || SEV.info;
                  const Icon = s.Icon;
                  return (
                    <div key={i} className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] ${s.cls}`}>
                      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span><span className="font-bold">{a.title}</span>{a.detail ? ` — ${a.detail}` : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {ai.alerts?.length > 0 && (
            <div data-testid="tendances-ia-alertes">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Alertes équipe / zone</div>
              <div className="space-y-1.5">
                {ai.alerts.map((a, i) => {
                  const s = SEV[a.severity] || SEV.info;
                  return (
                    <div key={i} className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-[12px]">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${s.cls}`}>{a.label}</span>
                      </div>
                      {a.recommendation && <div className="mt-1 text-muted-ink">{a.recommendation}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {ai.recommandations?.length > 0 && (
            <div data-testid="tendances-ia-recos">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Recommandations</div>
              <ul className="space-y-1">
                {ai.recommandations.map((r, i) => (
                  <li key={i} className="flex gap-2 text-[12px] text-ink">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1F9D55]" />{r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ ToggleBtn */
const ToggleBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)] ${active ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
    <Icon className="h-4 w-4" />{label}
  </button>
);

/* ------------------------------------------------------------------ PAGE */
export default function Tendances() {
  const [gran, setGran] = useState("semaine");
  const [indicator, setIndicator] = useState("multi");
  const [dimension, setDimension] = useState("global");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const params = useMemo(() => ({ indicator, granularity: gran, dimension }), [indicator, gran, dimension]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const { data } = await api.get("/trends", { params });
      setRes(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [params]);

  const loadAI = useCallback(async (refresh = false) => {
    setAiLoading(true);
    try {
      const { data } = await api.get("/trends/ai-insights", { params: { ...params, refresh } });
      setAi(data);
    } catch {
      setAi({ available: false, message: "Analyse IA momentanément indisponible. Réessayez." });
    } finally {
      setAiLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAI(false); }, [loadAI]);

  const series = res?.series || [];
  const data = res?.data || [];
  const partialBucket = (res?.buckets || []).find((b) => b.partial);
  const hasData = series.length > 0 && data.some((row) => series.some((s) => row[s.id] !== null && row[s.id] !== undefined));

  return (
    <>
      <PageHeader
        title="Tendances"
        subtitle="Évolution des taux de réussite — lecture décisionnelle et alertes IA."
        action={
          <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1" data-testid="tendances-granularite-toggle">
            {GRANS.map((g) => (
              <ToggleBtn key={g.v} active={gran === g.v} onClick={() => setGran(g.v)} icon={g.icon} label={g.label} testId={`tendances-granularite-${g.v}`} />
            ))}
          </div>
        }
      />

      <div className="mx-auto max-w-[1280px] space-y-6 px-4 py-5 sm:px-6 lg:px-8" data-testid="tendances-page">
        {/* Barre de contrôles : indicateur + dimension */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <LineChartIcon className="h-4 w-4 text-muted-ink" />
            <Select value={indicator} onValueChange={setIndicator}>
              <SelectTrigger className="h-10 w-[280px] max-w-full bg-white" data-testid="tendances-indicateur-select" aria-label="Indicateur">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INDICATORS.map((i) => (
                  <SelectItem key={i.v} value={i.v} data-testid={IND_TESTID[i.v]}>{i.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={dimension} onValueChange={setDimension}>
            <SelectTrigger className="h-10 w-[220px] max-w-full bg-white" data-testid="tendances-dimension-select" aria-label="Dimension">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIMENSIONS.map((d) => (
                <SelectItem key={d.v} value={d.v} data-testid={`tendances-dimension-option-${d.v}`}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {res?.note && (
            <span className="text-[11px] font-medium text-muted-ink sm:ml-auto" data-testid="tendances-note">{res.note}</span>
          )}
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3" data-testid="tendances-kpi-row">
          {loading
            ? KPI_META.map((k) => <Skeleton key={k.id} className="h-[104px] rounded-2xl" />)
            : KPI_META.map((k) => (
              <TrendKpi key={k.id} label={k.label} kpi={res?.kpis?.[k.id]} accent={k.accent} testId={k.testId} />
            ))}
        </div>

        {/* Contenu : chart (2 cols) + IA (1 col) */}
        <div className="grid gap-4 lg:grid-cols-3">
          <div className={`${card} p-5 lg:col-span-2`} data-testid="tendances-chart-card">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-sm font-bold text-ink">
                  {INDICATORS.find((i) => i.v === indicator)?.label}
                </div>
                <div className="text-[11px] text-muted-ink">
                  {DIMENSIONS.find((d) => d.v === (res?.dimension || dimension))?.label}
                  {partialBucket ? ` · dernière période (${partialBucket.label}) en cours` : ""}
                </div>
              </div>
            </div>

            {loading ? (
              <Skeleton className="h-72 w-full rounded-[var(--radius)]" />
            ) : error ? (
              <div className="flex h-72 flex-col items-center justify-center gap-3 rounded-[var(--radius)] border border-[rgba(214,69,69,0.3)] bg-[rgba(214,69,69,0.05)]" data-testid="tendances-chart-error">
                <span className="text-[13px] font-semibold text-[#9B2226]">Impossible de charger les tendances.</span>
                <Button variant="outline" size="sm" onClick={load} data-testid="tendances-chart-retry"><RefreshCw className="mr-1.5 h-4 w-4" />Réessayer</Button>
              </div>
            ) : res?.not_applicable ? (
              <div className="flex h-72 items-center justify-center rounded-[var(--radius)] border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-6 text-center text-[13px] text-muted-ink" data-testid="tendances-chart-empty">
                {res?.note || "Non applicable pour cette combinaison."}
              </div>
            ) : !hasData ? (
              <div className="flex h-72 items-center justify-center rounded-[var(--radius)] border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-6 text-center text-[13px] text-muted-ink" data-testid="tendances-chart-empty">
                Pas assez de données pour afficher une tendance sur cette période.
              </div>
            ) : (
              <div className="h-72 w-full" data-testid="tendances-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data} margin={{ top: 6, right: 14, left: 4, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(24,24,24,0.08)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="x" tick={{ fontSize: 11, fontWeight: 600, fill: "rgba(24,24,24,0.56)" }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
                    <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "rgba(24,24,24,0.56)" }} axisLine={false} tickLine={false} width={46} />
                    <RTooltip content={(p) => <ChartTooltip {...p} series={series} />} cursor={{ stroke: "rgba(238,114,46,0.25)", strokeWidth: 1 }} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} data-testid="tendances-chart-legend" />
                    {partialBucket && (
                      <ReferenceArea x1={partialBucket.label} x2={partialBucket.label} fill="rgba(24,24,24,0.05)" strokeOpacity={0} />
                    )}
                    {series.map((s) => (
                      <Line key={s.id} type="monotone" dataKey={s.id} name={s.label} stroke={s.color}
                        strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <AiPanel ai={ai} loading={aiLoading} onRefresh={() => loadAI(true)} />
        </div>

        {/* Tableau multi-indicateurs (vue combinée) */}
        {indicator === "multi" && !loading && hasData && (
          <div className={`${card} p-5`} data-testid="tendances-multi-table-card">
            <div className="mb-2 text-sm font-bold text-ink">Détail par période</div>
            <div className="overflow-x-auto">
              <Table data-testid="tendances-multi-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Période</TableHead>
                    <TableHead className="text-right">Réussite appels</TableHead>
                    <TableHead className="text-right">Spots décrochés</TableHead>
                    <TableHead className="text-right">Rebooking</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...data].reverse().map((row) => (
                    <TableRow key={row.key} className="hover:bg-[var(--surface-2)]" data-testid={`tendances-multi-row-${row.key}`}>
                      <TableCell className="font-semibold">
                        {row.x}
                        {row.partial && <span className="ml-2 rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-bold text-muted-ink">en cours</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtPct(row.reussite_appels)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtPct(row.spots_decroches)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtPct(row.rebooking)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
